import { useSyncExternalStore } from 'react';
import { onlineManager } from '@tanstack/react-query';

/**
 * Whether the browser currently believes it has no network.
 *
 * Deliberately reads React Query's `onlineManager` rather than
 * `navigator.onLine` directly: it is the exact same signal the query layer
 * gates fetching on (query-core's `canFetch` → `onlineManager.isOnline()`
 * under the default `networkMode: 'online'`), so what the banner says and what
 * the queries actually do can never disagree. It is also settable in tests via
 * `onlineManager.setOnline(false)` without stubbing globals.
 *
 * `useSyncExternalStore` (not useState + useEffect) so a connection change that
 * lands mid-render can't leave the UI on a stale value.
 *
 * Caveat worth knowing: the underlying browser signal reports transport, not
 * reachability. A captive portal or a dead API with a live wifi association
 * still reads as "online" — those failures surface as ordinary query errors,
 * which is why the error path stays and this only *adds* a state.
 */
export function useIsOffline(): boolean {
  return useSyncExternalStore(
    (onChange) => onlineManager.subscribe(onChange),
    () => !onlineManager.isOnline(),
    // Server/first-paint fallback: assume online. Rendering an offline banner
    // during hydration for a user who is fine is worse than a beat of delay.
    () => false,
  );
}
